import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Box, Checkbox, FormControlLabel, Grid, IconButton, Link, Typography } from "@mui/material";
import { useState } from "react";
import PropTypes from "prop-types";
import { NEW_USER, RETURN_USER } from "./analyticsData";

const slicerSx = {
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 1,
    p: 1.5,
    mb: 2
};

const checkboxSx = { p: 0.25, mr: 0.5 };

const TIMELINE_HEIGHT = 380;
const TIMELINE_HEIGHT_COMPACT = 222;

const scrollbarSx = {

    scrollbarWidth: "thin",
    scrollbarColor: "#161e54 #E4E7F2",
    "&::-webkit-scrollbar": { width: 10 },
    "&::-webkit-scrollbar-track": {
        backgroundColor: "#E4E7F2",
        borderRadius: 5
    },
    "&::-webkit-scrollbar-thumb": {
        backgroundColor: "#161e54",
        borderRadius: 5
    },
    "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#0f1540" }
};

const scrollAreaSx = (height) => ({
    height,
    overflowY: "scroll",
    pr: 0.5,
    ...scrollbarSx
});

const fillSlicerSx = {
    flex: 1,
    minHeight: 0,
    mb: 0,
    display: "flex",
    flexDirection: "column"
};

const gridFillItemSx = {
    minHeight: 0,
    display: "flex",
    flexDirection: "column"
};

const labelSx = {
    m: 0,
    "& .MuiFormControlLabel-label": { fontSize: "0.8rem" }
};

const toggleValues = (current, values, checked) => {
    const next = new Set(current);
    values.forEach(value => (checked ? next.add(value) : next.delete(value)));
    return [...next];
};

const Slicer = ({ title, children, sx }) => (
    <Box sx={{ ...slicerSx, ...sx }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>{title}</Typography>
        {children}
    </Box>
);

Slicer.propTypes = {
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    children: PropTypes.node,
    sx: PropTypes.object,
};

const TriStateRow = ({ label, selectedCount, total, onToggle, expandable, expanded, onExpand, indent = 0, rowSx }) => (
    <Box sx={{ display: "flex", alignItems: "center", pl: indent, ...rowSx }}>
        {expandable ? (
            <IconButton size="small" sx={{ p: 0.25 }} onClick={onExpand}>
                {expanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
            </IconButton>
        ) : (
            <Box sx={{ width: 22 }} />
        )}
        <FormControlLabel
            sx={labelSx}
            control={
                <Checkbox
                    size="small"
                    sx={checkboxSx}
                    checked={total > 0 && selectedCount === total}
                    indeterminate={selectedCount > 0 && selectedCount < total}
                    onChange={event => onToggle(event.target.checked)}
                />
            }
            label={label}
        />
    </Box>
);

TriStateRow.propTypes = {
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    selectedCount: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    onToggle: PropTypes.func.isRequired,
    expandable: PropTypes.bool,
    expanded: PropTypes.bool,
    onExpand: PropTypes.func,
    indent: PropTypes.number,
    rowSx: PropTypes.object,
};

const TimelineSlicer = ({ timeline, selectedMonths, onChange, tall = false, scrollHeight }) => {
    const [collapsed, setCollapsed] = useState(() => new Set());
    const rowSx = tall ? { py: 1.25 } : undefined;

    const isCollapsed = (id) => collapsed.has(id);
    const toggleCollapse = (id) => {
        const next = new Set(collapsed);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setCollapsed(next);
    };

    const countSelected = (keys) => keys.filter(key => selectedMonths.includes(key)).length;

    return (
        <Slicer title="Timeline" sx={tall ? { py: 2.5 } : undefined}>
            <Box sx={scrollHeight ? scrollAreaSx(scrollHeight) : undefined}>
            {timeline.map(({ year, quarters }) => {
                const yearKeys = quarters.flatMap(q => q.months.map(m => m.key));
                return (
                    <Box key={year}>
                        <TriStateRow
                            label={year}
                            selectedCount={countSelected(yearKeys)}
                            total={yearKeys.length}
                            onToggle={checked => onChange(toggleValues(selectedMonths, yearKeys, checked))}
                            expandable
                            expanded={!isCollapsed(String(year))}
                            onExpand={() => toggleCollapse(String(year))}
                            rowSx={rowSx}
                        />
                        {!isCollapsed(String(year)) && quarters.map(quarter => {
                            const quarterId = `${year}-${quarter.label}`;
                            const quarterKeys = quarter.months.map(m => m.key);
                            return (
                                <Box key={quarterId}>
                                    <TriStateRow
                                        label={quarter.label}
                                        selectedCount={countSelected(quarterKeys)}
                                        total={quarterKeys.length}
                                        onToggle={checked => onChange(toggleValues(selectedMonths, quarterKeys, checked))}
                                        expandable
                                        expanded={!isCollapsed(quarterId)}
                                        onExpand={() => toggleCollapse(quarterId)}
                                        indent={2}
                                        rowSx={rowSx}
                                    />
                                    {!isCollapsed(quarterId) && quarter.months.map(month => (
                                        <TriStateRow
                                            key={month.key}
                                            label={month.label}
                                            selectedCount={countSelected([month.key])}
                                            total={1}
                                            onToggle={checked => onChange(toggleValues(selectedMonths, [month.key], checked))}
                                            indent={4}
                                            rowSx={rowSx}
                                        />
                                    ))}
                                </Box>
                            );
                        })}
                    </Box>
                );
            })}
            </Box>
        </Slicer>
    );
};

TimelineSlicer.propTypes = {
    timeline: PropTypes.arrayOf(PropTypes.shape({
        year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        quarters: PropTypes.array,
    })).isRequired,
    selectedMonths: PropTypes.arrayOf(PropTypes.string).isRequired,
    onChange: PropTypes.func.isRequired,
    tall: PropTypes.bool,
    scrollHeight: PropTypes.number,
};

const CheckboxSlicer = ({ title, options, selected, onChange }) => (
    <Slicer title={title}>
        {options.map(option => (
            <Box key={option.value} sx={{ display: "flex", alignItems: "center" }}>
                <FormControlLabel
                    sx={labelSx}
                    control={
                        <Checkbox
                            size="small"
                            sx={checkboxSx}
                            checked={selected.includes(option.value)}
                            onChange={event => onChange(toggleValues(selected, [option.value], event.target.checked))}
                        />
                    }
                    label={option.label}
                />
            </Box>
        ))}
    </Slicer>
);

CheckboxSlicer.propTypes = {
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })).isRequired,
    selected: PropTypes.arrayOf(PropTypes.string).isRequired,
    onChange: PropTypes.func.isRequired,
};

const UserListSlicer = ({ users, selected, onChange }) => (
    <Slicer title="User List" sx={fillSlicerSx}>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5, ...scrollbarSx }}>
            {users.map(user => (
                <Box key={user.mail} sx={{ display: "flex", alignItems: "center" }}>
                    <FormControlLabel
                        sx={labelSx}
                        control={
                            <Checkbox
                                size="small"
                                sx={checkboxSx}
                                checked={selected.includes(user.mail)}
                                onChange={event => onChange(toggleValues(selected, [user.mail], event.target.checked))}
                            />
                        }
                        label={user.name}
                    />
                </Box>
            ))}
        </Box>
    </Slicer>
);

UserListSlicer.propTypes = {
    users: PropTypes.arrayOf(PropTypes.shape({
        mail: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })).isRequired,
    selected: PropTypes.arrayOf(PropTypes.string).isRequired,
    onChange: PropTypes.func.isRequired,
};

const FilterPanel = ({
    timeline, users, filters, setFilters,
    showFormType = false, showUserType = false, showUserList = false,
    tallTimeline = false, compactTimeline = false, fillHeight = false
}) => {
    const hasActiveFilter = filters.months.length || filters.formTypes.length
        || filters.userTypes.length || filters.users.length;

    const update = (key) => (value) => setFilters(prev => ({ ...prev, [key]: value }));

    return (
        <Grid
            container
            direction="column"
            wrap="nowrap"
            sx={fillHeight ? { height: "100%" } : undefined}
        >
            {hasActiveFilter ? (
                <Grid item>
                    <Box sx={{ mb: 1, textAlign: "right" }}>
                        <Link
                            component="button"
                            variant="caption"
                            underline="hover"
                            onClick={() => setFilters({ months: [], formTypes: [], userTypes: [], users: [] })}
                        >
                            Clear filters
                        </Link>
                    </Box>
                </Grid>
            ) : null}

            <Grid item>
                <TimelineSlicer
                    timeline={timeline}
                    selectedMonths={filters.months}
                    onChange={update("months")}
                    tall={tallTimeline}
                    scrollHeight={tallTimeline ? TIMELINE_HEIGHT : (compactTimeline ? TIMELINE_HEIGHT_COMPACT : undefined)}
                />
            </Grid>

            {showFormType && (
                <Grid item>
                    <CheckboxSlicer
                        title="Form Type"
                        options={[
                            { value: "custom", label: "Custom" },
                            { value: "standard", label: "Standard" }
                        ]}
                        selected={filters.formTypes}
                        onChange={update("formTypes")}
                    />
                </Grid>
            )}

            {showUserType && (
                <Grid item>
                    <CheckboxSlicer
                        title="User Type"
                        options={[
                            { value: NEW_USER, label: NEW_USER },
                            { value: RETURN_USER, label: RETURN_USER }
                        ]}
                        selected={filters.userTypes}
                        onChange={update("userTypes")}
                    />
                </Grid>
            )}

            {showUserList && (
                <Grid item xs sx={gridFillItemSx}>
                    <UserListSlicer
                        users={users}
                        selected={filters.users}
                        onChange={update("users")}
                    />
                </Grid>
            )}
        </Grid>
    );
};

FilterPanel.propTypes = {
    timeline: PropTypes.arrayOf(PropTypes.shape({
        year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        quarters: PropTypes.array,
    })).isRequired,
    users: PropTypes.arrayOf(PropTypes.shape({
        mail: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })).isRequired,
    filters: PropTypes.shape({
        months: PropTypes.arrayOf(PropTypes.string),
        formTypes: PropTypes.arrayOf(PropTypes.string),
        userTypes: PropTypes.arrayOf(PropTypes.string),
        users: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    setFilters: PropTypes.func.isRequired,
    showFormType: PropTypes.bool,
    showUserType: PropTypes.bool,
    showUserList: PropTypes.bool,
    tallTimeline: PropTypes.bool,
    compactTimeline: PropTypes.bool,
    fillHeight: PropTypes.bool,
};

export default FilterPanel;
